import { useCallback, useEffect, useState } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import type { Alert } from '../types';

interface UseTenantAlertsOptions {
  onAlert?: (alert: Alert) => void;
}

interface UseTenantAlertsReturn {
  isConnected: boolean;
  isSubscribed: boolean;
  lastAlert: Alert | null;
}

/**
 * Subscribe to alert stream events for the current tenant.
 */
export function useTenantAlertsWebSocket(
  options: UseTenantAlertsOptions = {},
): UseTenantAlertsReturn {
  const { onAlert } = options;
  const { currentTenant, user } = useAuth();
  const { state, addObserver, removeObserver } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastAlert, setLastAlert] = useState<Alert | null>(null);

  const tenantId = currentTenant?.id || user?.tenantId || null;

  const handleEvent = useCallback(
    (data: Alert, action: string) => {
      if (action !== 'create') {
        return;
      }

      setLastAlert(data);
      onAlert?.(data);
    },
    [onAlert],
  );

  useEffect(() => {
    if (!tenantId || !state.isConnected) {
      setIsSubscribed(false);
      return;
    }

    const observerId = addObserver(
      'alert',
      { data: { target: tenantId } },
      handleEvent,
      {
        includeCreateEvents: true,
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
      },
    );
    setIsSubscribed(true);

    return () => {
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [tenantId, state.isConnected, addObserver, removeObserver, handleEvent]);

  return {
    isConnected: state.isConnected,
    isSubscribed,
    lastAlert,
  };
}

export default useTenantAlertsWebSocket;
